import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// Decode JWT payload without verifying signature — just to read exp
function jwtExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('mfa_enroll');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState(() => {
    try { return JSON.parse(localStorage.getItem('features') || '{}'); } catch { return {}; }
  });
  const [isOperator, setIsOperator] = useState(() => localStorage.getItem('is_operator') === '1');
  // Roles que exigen 2FA: mientras esté en true, el token que hay guardado solo sirve
  // para activarlo. Se persiste para que un F5 no devuelva al usuario al resto de la app
  // (igual el backend rechazaría todo, pero la UI quedaría rota y sin explicación).
  const [mfaEnroll, setMfaEnroll] = useState(() => localStorage.getItem('mfa_enroll') === '1');

  const logout = useCallback(() => {
    clearSession();
    localStorage.removeItem('features');
    localStorage.removeItem('is_operator');
    setUser(null);
    setFeatures({});
    setIsOperator(false);
    setMfaEnroll(false);
  }, []);

  // Trae los módulos habilitados del tenant desde el backend (fuente de verdad)
  const refreshFeatures = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/config/features`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      const f = d.features || {};
      const op = !!d.is_operator;
      setFeatures(f); setIsOperator(op);
      localStorage.setItem('features', JSON.stringify(f));
      localStorage.setItem('is_operator', op ? '1' : '0');
    } catch { /* noop */ }
  }, []);

  const checkToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const exp = jwtExpiry(token);
    if (exp && Date.now() >= exp) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const exp = jwtExpiry(token);
        if (exp && Date.now() >= exp) {
          // Token already expired on load — clear and stay at login
          clearSession();
        } else {
          setUser(JSON.parse(savedUser));
          refreshFeatures();
        }
      } catch {}
    }
    setLoading(false);
  }, [refreshFeatures]);

  // Re-check expiry when user returns to the tab
  useEffect(() => {
    document.addEventListener('visibilitychange', checkToken);
    return () => document.removeEventListener('visibilitychange', checkToken);
  }, [checkToken]);

  // Global event dispatched by apiFetch helpers when backend returns 401/403 token error
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('itil-session-expired', handler);
    return () => window.removeEventListener('itil-session-expired', handler);
  }, [logout]);

  // needsMfa: el backend emitió un token acotado a la activación del 2FA.
  const login = (token, userData, needsMfa = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (needsMfa) localStorage.setItem('mfa_enroll', '1');
    else localStorage.removeItem('mfa_enroll');
    setUser(userData);
    setMfaEnroll(!!needsMfa);
    // Con el token restringido, /config/features responde 403: pedirlo solo ensucia
    // la consola y no aporta nada hasta que active el segundo factor.
    if (!needsMfa) refreshFeatures();
  };

  // El backend devuelve el token completo al confirmar el código: se reemplaza el
  // restringido y recién ahí se libera el resto de la aplicación.
  const completeMfaEnrollment = (token, userData) => {
    localStorage.setItem('token', token);
    if (userData) localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('mfa_enroll');
    if (userData) setUser(userData);
    setMfaEnroll(false);
    refreshFeatures();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, features, isOperator, refreshFeatures, mfaEnroll, completeMfaEnrollment }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
